import React, { useCallback, useEffect, useState } from 'react';
import { initializePaddle, InitializePaddleOptions } from '@paddle/paddle-js';
import { useOutletContext } from 'react-router-dom';
import { User } from '@back4app2/sdk';
import Modal from '../Modal';
import { AmplitudeEvent, trackEvent } from '../../utils/amplitude';

interface AgentPlan {
  id: string;
  planId?: string;
  name: string;
  priceLabel: string;
  priceSuffix: string;
  description: string;
  features: string[];
  badge?: string;
  enabled?: boolean;
}

interface AgentUpgradeModalProps {
  isOpen: boolean;
  onClose: () => void;
  plans?: AgentPlan[];
  appId?: string;
}

const DEFAULT_PLANS: AgentPlan[] = [
  {
    id: 'pri_01jkbsrsdhscby3m0aembgvk70',
    planId: 't9MIqjNzm7',
    name: 'Pro Agent',
    priceLabel: '$25',
    priceSuffix: '/ month',
    description: 'Unlock the full potential of your AI Agent.',
    features: [
      '500 monthly AI Agent prompts',
      'Priority support',
      'Cloud Platform Included',
      'Database, APIs, Storage & more'
    ],
    badge: 'Popular',
  }
];

const AgentUpgradeModal: React.FC<AgentUpgradeModalProps> = ({
  isOpen,
  onClose,
  plans = DEFAULT_PLANS,
  appId,
}) => {
  const [paddle, setPaddle] = useState<any>(undefined);
  const [isLoadingPaddle, setIsLoadingPaddle] = useState(false);
  const [selectedPlanId, setSelectedPlanId] = useState<string | undefined>(undefined);
  const [showCheckout, setShowCheckout] = useState(false);
  const [openRetryCount, setOpenRetryCount] = useState(0);
  const closeCheckout = useCallback(() => {
    setShowCheckout(false);
    setSelectedPlanId(undefined);
    setOpenRetryCount(0);
  }, []);
  const { user } = useOutletContext<{ user?: User }>();
  const customerEmail = user?.username;
  const visiblePlans = plans.filter((plan) => plan.enabled !== false);

  const paddleEventCallback = useCallback(async (data: any) => {
    if (data.name === 'checkout.completed') {
      const paymentData = {
        appId: data.data.custom_data.app_id,
        customerId: data.data.customer.id,
        planName: data.data.items[0].product.name,
        transactionId: data.data.transaction_id,
        checkoutId: data.data.id,
        results: data.data,
        planId: data.data.custom_data.plan_id,
        email: data.data.customer.email,
      };

      try {
        const res = await fetch(`${process.env.REACT_APP_BACK4APP_CHECKOUT_URL}/save-subscription`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(paymentData),
        });
        if (!res.ok) {
          throw new Error(`save-subscription failed with status ${res.status}`);
        }

        trackEvent(AmplitudeEvent.AGENT_PLAN_MODAL_PAY_SUCCESS, {
          appId: paymentData.appId,
          planId: paymentData.planId,
          transactionId: paymentData.transactionId,
          checkoutId: paymentData.checkoutId,
        });
      } catch (error) {
        console.error('Error saving subscription:', error);
      }

      closeCheckout();
      onClose?.();
    }
    
    if (data.name === 'checkout.closed') {
      closeCheckout();
      onClose?.();
    }
  }, [onClose, closeCheckout]);

  useEffect(() => {
    if (!isOpen) return;
    if (paddle || isLoadingPaddle) return;

    const paddleOptions: InitializePaddleOptions = {
      token: process.env.REACT_APP_PADDLE_TOKEN || 'test_0270ab179b4f4abd7aa228c7014',
      environment: process.env.REACT_APP_SENTRY_ENV === 'production' ? 'production' : 'sandbox',
      pwCustomer: {},
      eventCallback: paddleEventCallback
    };

    setIsLoadingPaddle(true);
    initializePaddle(paddleOptions)
      .then((p) => {
        setPaddle(p);
      })
      .catch((err) => {
        console.error('Paddle initialization failed:', err);
      })
      .finally(() => setIsLoadingPaddle(false));
  }, [isOpen, paddle, isLoadingPaddle, paddleEventCallback]);

  useEffect(() => {
    if (!isOpen) {
      closeCheckout();
    }
  }, [isOpen, closeCheckout]);

  // Track modal open event
  useEffect(() => {
    if (isOpen) {
      trackEvent(AmplitudeEvent.AGENT_PLAN_MODAL_OPEN);
    }
  }, [isOpen]);

  const handleUpgrade = useCallback(
    (priceId: string) => {
      if (!paddle || !paddle.Checkout) {
        console.error('Paddle not initialized');
        return;
      }
      trackEvent(AmplitudeEvent.AGENT_PLAN_MODAL_PAY_CLICK, { priceId });
      setSelectedPlanId(priceId);
      setShowCheckout(true);
    },
    [paddle]
  );

  useEffect(() => {
    if (!showCheckout || !selectedPlanId || !paddle) return;

    const plan = visiblePlans.find((p) => p.id === selectedPlanId);
    const planCustomId = plan?.planId || selectedPlanId;

    const timer = setTimeout(() => {
      if (!paddle || !paddle.Checkout || typeof paddle.Checkout.open !== 'function') {
        console.error('Paddle not ready');
        closeCheckout();
        return;
      }

      try {
        paddle.Checkout.open({
          items: [{
            priceId: process.env.REACT_APP_SENTRY_ENV === 'production' ? selectedPlanId : 'pri_01jjykwj65y5de1vcv5xaryw8g',
            quantity: 1
          }],
          settings: {
            displayMode: 'inline',
            theme: 'light',
            locale: 'en',
            variant: 'one-page',
            frameTarget: 'checkout-container',
            frameInitialHeight: 450,
            frameStyle: 'width: 100%; min-width: 312px; max-height: 80vh; background-color: #f9f9f9; border: none;'
          },
          allowLogout: false,
          customData: { appId, planId: planCustomId },
          customer: customerEmail ? { email: customerEmail } : undefined,
        });
        
        setSelectedPlanId(undefined);
        setOpenRetryCount(0);
      } catch (err) {
        console.error('Error opening Paddle checkout:', err);
        closeCheckout();
      }
    }, 200);

    return () => clearTimeout(timer);
  }, [showCheckout, selectedPlanId, paddle, customerEmail, appId, visiblePlans, openRetryCount, closeCheckout]);

  if (!isOpen) {
    return null;
  }

  return (
    <>
      {!showCheckout && (
        <>
          <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-[10000]" onClick={onClose} />
          <div className="fixed inset-0 z-[10001] flex items-center justify-center px-4 py-6">
            <div className="w-full max-w-[720px] bg-[#0f172a]/90 backdrop-blur-2xl border border-white/10 rounded-xl shadow-[0_14px_48px_rgba(0,0,0,0.45)] p-6">
              <div className="flex items-start justify-between mb-6 gap-4">
                <div>
                  <h2 className="text-2xl font-bold text-white">Upgrade to Pro</h2>
                  <p className="text-white/70 text-sm mt-1">Unlock the full potential of your AI Agent.</p>
                </div>
                <button
                  onClick={onClose}
                  className="text-white/60 hover:text-white transition-colors"
                  aria-label="Close upgrade modal"
                >
                  ✕
                </button>
              </div>

              <div className="grid grid-cols-1 gap-4">
                {visiblePlans.map((plan) => (
                  <div
                    key={plan.id}
                    className={`relative rounded-2xl border border-white/10 bg-white/5 p-5 flex flex-col gap-4 ${
                      plan.badge ? 'shadow-[0_16px_48px_rgba(34,197,94,0.25)]' : 'shadow-[0_8px_32px_rgba(0,0,0,0.25)]'
                    }`}
                  >
                    {plan.badge && (
                      <span className="absolute top-0 right-0 bg-gradient-to-r from-[#22c55e] to-[#16a34a] text-xs font-semibold text-white px-3 py-1 rounded-bl-lg rounded-tr-2xl">
                        {plan.badge}
                      </span>
                    )}

                    <div>
                      <h3 className="text-xl font-bold text-white">{plan.name}</h3>
                      <p className="text-white/70 text-sm mt-1">{plan.description}</p>
                    </div>

                    <div>
                      <div className="text-3xl font-bold text-white">{plan.priceLabel}</div>
                      <div className="text-white/60 text-sm">{plan.priceSuffix}</div>
                    </div>

                    <div className="space-y-2 text-sm text-white/80">
                      {plan.features.map((feature, idx) => (
                        <div key={idx} className="flex items-start gap-2">
                          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" className="mt-0.5 text-[#22c55e]">
                            <path d="M5 13l4 4L19 7" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                          </svg>
                          <span>{feature}</span>
                        </div>
                      ))}
                    </div>

                    <button
                      onClick={() => handleUpgrade(plan.id)}
                      disabled={isLoadingPaddle || showCheckout}
                      className={`mt-auto px-4 py-3 rounded-lg text-sm font-semibold transition-all ${
                        plan.badge
                          ? 'bg-[#22c55e] text-white hover:bg-[#16a34a]'
                          : 'border border-white/20 text-white hover:bg-white/5'
                      } disabled:opacity-60 disabled:cursor-not-allowed`}
                    >
                      Upgrade Now
                    </button>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </>
      )}

      {showCheckout && (
        <>
          <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-[10001]" />
          <div className="fixed inset-0 z-[10002] flex items-center justify-center px-4 py-6 pt-20">
            <div className="w-full max-w-4xl mx-auto bg-white rounded-lg shadow-[0_18px_60px_rgba(0,0,0,0.45)] overflow-hidden relative">
              <button
                onClick={() => {
                  closeCheckout();
                  onClose();
                }}
                className="absolute top-4 right-4 z-50 text-gray-600 hover:text-gray-900 text-2xl font-light w-8 h-8 flex items-center justify-center rounded-full hover:bg-gray-100 transition-colors"
                aria-label="Close modal"
              >
                ✕
              </button>
              <div 
                id="checkout-container" 
                className="checkout-container w-full min-h-[450px] bg-white"
                style={{ maxHeight: '80vh', overflowY: 'auto' }}
              ></div>
            </div>
          </div>
        </>
      )}
    </>
  );
};

export default AgentUpgradeModal;


